import React, { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { LoadingButton } from "@mui/lab";
import { styled } from "@mui/material/styles";

import AttachFileIcon from "@mui/icons-material/AttachFile";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import FileOutlined from "../../assets/FileOutlined.svg";
import TrashOutlined from "../../assets/TrashIconOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";

import {
  uploadWaiverRequestDoc,
  uploadFileToCloud,
  fetchWaiverRequestDoc,
} from "../../services/network";

import MitigationFactorSelect from "./MitigationFactorSelect";
import {
  WaiverStatus,
  MIN_MITIGATION_FACTORS,
  getFullName,
  asMitigationFactorArray,
  persistNewWaiver,
  softDeleteUploadedDocument,
} from "./utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export interface WaiverDocument {
  fileName: string;
  filePath: string;
}

// Fully independent from the processor's `mitigationFactor`/`description` on
// WaiverRequestData below — the underwriter/credit-committee's compensating
// factors and comments are a separate field the processor's data is never
// mutated through. `approvedBy`/`rejectedBy`/`approvedConditionallyBy` (and
// their `*On` timestamps) are only ever set for the outcome actually chosen;
// they accumulate across repeated act→reset→act cycles rather than being
// overwritten, so each outcome type keeps its own audit trail.
export interface WaiverAction {
  compensatingFactor: string[];
  comments: string;
  approvedBy?: string;
  approvedOn?: string;
  rejectedBy?: string;
  rejectedOn?: string;
  approvedConditionallyBy?: string;
  approvedConditionallyOn?: string;
}

export interface WaiverRequestData {
  // Present only on loan-level General Waivers (TA-11746), which have no
  // specific test to key off of; per-test waivers use takeoutPartner +
  // testTitle instead. See getWaiverListField/waiverMatcher in utils.ts.
  id?: string;
  takeoutPartner?: string;
  testTitle?: string;
  mitigationFactor: string[];
  description: string;
  docs: WaiverDocument[];
  status: string;
  createdBy: string;
  createdOn: string;
  underwriterAction?: WaiverAction;
}

export default function WaiverRequestForm({
  currentTabKey,
  testTitle,
  onSubmit,
  onCancel,
}: {
  currentTabKey?: string;
  testTitle?: string;
  onSubmit?: (data: WaiverRequestData) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const { dynamicConfig, decodedTokenData } = useContext(RootContext) ?? {};
  const { loanDetails, setLoanDetails } = useContext(LoansContext) ?? {};
  const { loanId } = useParams<{ loanId?: string }>();

  const [mitigationFactor, setMitigationFactor] = useState<string[]>([]);
  const [description, setDescription] = useState("");
  const isOtherSelected = mitigationFactor.includes("Other");
  const [documents, setDocuments] = useState<WaiverDocument[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isDragActive, setIsDragActive] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [downloadingPath, setDownloadingPath] = useState("");

  const downloadFile = async (filePath: string) => {
    setDownloadingPath(filePath);
    try {
      const urlData = await fetchWaiverRequestDoc(dynamicConfig?.fileDownloadUrl, {
        path: filePath,
      });
      window.open(urlData.data, "_blank");
    } catch (err) {
      console.error("Something went wrong while downloading file. ERROR:", err);
    } finally {
      setDownloadingPath("");
    }
  };

  const uploadSingleFile = async (file: File): Promise<WaiverDocument> => {
    const filePath = `sizerloan/${loanId ?? "unknown"}/WaiverDoc/${currentTabKey ?? "unknown"}/${testTitle ?? "unknown"}/${file.name}`;
    const urlData = await uploadWaiverRequestDoc(
      dynamicConfig?.fileUploadUrl ?? "",
      {
        path: filePath,
        originatorId: null,
        originatorLoanId: null,
        internal: false,
      },
    );
    const formData = new FormData();
    formData.append("file", file);
    await uploadFileToCloud(formData, urlData?.data);
    const strippedFilePath = `${urlData?.data?.split("sizerloan")?.[0]}${filePath}`;
    return { fileName: file.name, filePath: strippedFilePath };
  };

  const uploadFiles = async (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (!files.length) return;

    setIsUploading(true);
    setUploadError("");
    try {
      const uploadedDocs = await Promise.all(files.map(uploadSingleFile));
      setDocuments((prev) => [...prev, ...uploadedDocs]);
    } catch (err) {
      setUploadError(t("waiverUploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length) await uploadFiles(files);
  };

  const handleDragOver = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = async (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    setIsDragActive(false);
    if (event.dataTransfer.files) await uploadFiles(event.dataTransfer.files);
  };

  const handleDeleteDocument = (index: number) => {
    const doc = documents[index];
    setDocuments((prev) => prev.filter((_, docIdx) => docIdx !== index));
    softDeleteUploadedDocument(loanId, loanDetails, setLoanDetails, doc).catch(
      (err) =>
        console.error("Something went wrong while deleting the uploaded file. ERROR:", err),
    );
  };

  const handleSubmit = async () => {
    const waiverRequest: WaiverRequestData = {
      takeoutPartner: currentTabKey,
      testTitle,
      mitigationFactor: asMitigationFactorArray(mitigationFactor),
      description,
      docs: documents,
      status: WaiverStatus.PENDING,
      createdBy: getFullName(decodedTokenData),
      createdOn: new Date().toISOString(),
    };

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await persistNewWaiver(loanId, loanDetails, setLoanDetails, waiverRequest);
      onSubmit?.(waiverRequest);
    } catch (err) {
      setSubmitError(t("waiverSubmitFailed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      data-testid="waiver-request-form"
      sx={{
        marginTop: "24px",
        borderRadius: "6px",
        border: "1px solid #B8B8C7",
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "10px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            border: "1px solid #DBE2FF",
            background: "#F8F9FD",
            width: "auto"
          }}
        >
          <h3
            style={{
              color: "#000",
              fontFamily: "Poppins",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              margin: 0,
              padding: 0,
            }}
          >
            {t("requestingWaiver")}
          </h3>
        </Grid>
      </Grid>
      <Grid container style={{ padding: "16px",}}>
        <Grid item xs={12}>
          <FormControl fullWidth size="small">
            <label
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
              htmlFor="mitigation-factor-label"
            >
              {t("mitigationFactor")} <sup style={{ color: "red" }}>*</sup>
            </label>
            <MitigationFactorSelect
              value={mitigationFactor}
              onChange={(selected) => {
                setMitigationFactor(selected);
                if (!selected.includes("Other")) {
                  setDescription("");
                }
              }}
              labelId="mitigation-factor-label"
              testId="mitigation-factor-select"
            />
          </FormControl>
        </Grid>

        {isOtherSelected ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 300,
              }}
            >
              {t("waiverDescription")}
              <sup style={{ color: "red" }}>*</sup>
            </span>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              data-testid="waiver-description-input"
              style={{ background: "#F8F9FD",}}
            />
          </Grid>
        ) : null}

        <Grid item xs={12} style={{ paddingTop: "16px" }}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 300,
            }}
          >
            {t("supportDocument")}
          </span>
          <LoadingButton
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<AttachFileIcon />}
            loading={isUploading}
            data-testid="waiver-upload-button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              background: isDragActive ? "#EAEFFF" : "#F8F9FD",
              borderColor: isDragActive ? "#3E5EEC" : undefined,
              borderStyle: "dashed",
            }}
          >
            <span>{t("dragAndDropOrUpload")}</span>
            <VisuallyHiddenInput
              type="file"
              accept=".pdf, .docx, .jpeg, .jpg, .png"
              onChange={handleFileUpload}
              multiple
            />
          </LoadingButton>
          {uploadError ? (
            <div
              style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px" }}
            >
              {uploadError}
            </div>
          ) : null}
        </Grid>

        {documents.length > 0 ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span>
              {t("textUploaded", { count: documents.length })}
            </span>
            {documents.map((doc, index) => (
              <div
                key={`${doc.fileName}-${index}`}
                data-testid="uploaded-document-item"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  marginBottom: "6px",
                  borderRadius: "4px",
                  background: "white",
                  border: "1px solid #B8B8C7",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={FileOutlined}
                    alt="file-icon"
                    style={{ width: "16px", height: "16px", marginRight: "8px" }}
                  />
                  <span style={{ fontSize: "14px" }}>{doc.fileName}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <IconButton
                    size="small"
                    aria-label="download"
                    onClick={() => downloadFile(doc.filePath)}
                    disabled={downloadingPath === doc.filePath}
                    data-testid="download-document-button"
                  >
                    {downloadingPath === doc.filePath ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FileDownloadOutlinedIcon fontSize="small" style={{ color: "#383847" }} />
                    )}
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={() => handleDeleteDocument(index)}
                    data-testid="delete-document-button"
                  >
                    <img src={TrashOutlined} alt="delete-icon" style={{ width: "16px", height: "16px" }} />
                  </IconButton>
                </div>
              </div>
            ))}
          </Grid>
        ) : null}

        {submitError ? (
          <Grid item xs={12} style={{ paddingTop: "8px" }}>
            <div style={{ color: "#ef4444", fontSize: "12px" }}>
              {submitError}
            </div>
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "16px" }}
        >
          <Button
            variant="text"
            onClick={onCancel}
            disabled={isSubmitting}
            data-testid="waiver-cancel-button"
          >
            {t("cancel")}
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={
              mitigationFactor.length < MIN_MITIGATION_FACTORS ||
              (isOtherSelected && !description.trim())
            }
            data-testid="waiver-submit-button"
          >
            {t("submitWaiver")}
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
}
